import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { v2Client as axios, v2TeamClient as TeamClient } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { authHeaders, currentUserId } from "@/v2/lib/auth";
import { APIRoutes } from "@/utils/constants";
import { roleIdFromToken } from "@/utils/userRole";

/**
 * Data layer for the v2 header. Mirrors the v1 header's network behavior
 * (schedule search, channels, favorite teams, unread count, viewer role)
 * but through TanStack Query. All keys are prefixed ['v2', 'header', ...]
 * so the header can invalidate its own cache without touching page state.
 */

export type ScheduleGame = {
  game_id: string | number;
  game_date?: string;
  game_date_time?: string;
  card_image_url_wide?: string;
  card_image_url?: string;
  away_team?: string;
  away_team_logo?: string;
  away_team_mascot?: string;
  home_team?: string;
  home_team_logo?: string;
  home_team_mascot?: string;
  facility_name?: string;
};

export type Channel = {
  channel_id: string | number;
  channel_name: string;
  channel_logo_url?: string;
};

export type FavoriteTeam = {
  team_id: string | number;
  team_name?: string;
  team_mascot?: string;
  team_logo_url?: string;
};

export type ScheduleStatus = "live" | "vod";

export type SearchFilters = {
  search: string;
  team: string;
  channel: string;
};

function scheduleParams(
  status: ScheduleStatus,
  customer: string,
  filters: SearchFilters,
  limit: number
) {
  const params: Record<string, string | number> = {
    customer,
    status,
    page: 1,
    search: filters.search,
    limit,
  };
  if (filters.team) params.team = filters.team;
  if (filters.channel) params.channel = filters.channel;
  return params;
}

/**
 * Live/upcoming or on-demand schedule results for the search dropdown.
 * `enabled` lets the header defer the request until the panel is open.
 */
export function useScheduleSearch(
  status: ScheduleStatus,
  customer: string,
  filters: SearchFilters,
  { limit = 5, enabled = true }: { limit?: number; enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ["v2", "header", "schedule", status, customer, filters, limit],
    enabled: enabled && Boolean(customer),
    queryFn: async ({ signal }) => {
      const res = await TeamClient.get<{ data: ScheduleGame[] }>(
        APIRoutes.TeamEventsList,
        {
          params: scheduleParams(status, customer, filters, limit),
          headers: authHeaders(),
          signal,
        }
      );
      return res.data?.data ?? [];
    },
  });
}

/** Tenant channels shown as filter checkboxes in the search dropdown. */
export function useChannels(
  customer: string,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ["v2", "header", "channels", customer],
    enabled: enabled && Boolean(customer),
    queryFn: async ({ signal }) => {
      const res = await TeamClient.get<{ data: Channel[] }>(
        APIRoutes.ChannelOption.replace(":id", customer),
        { headers: authHeaders(), signal }
      );
      return res.data?.data ?? [];
    },
  });
}

/** The signed-in viewer's favorite teams (empty for logged-out viewers). */
export function useFavoriteTeams({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["v2", "header", "favorite-teams", currentUserId()],
    enabled,
    queryFn: async ({ signal }) => {
      const res = await axios.get<{
        data: { UserFavouriteTeam?: FavoriteTeam[] } | null;
      }>(APIRoutes.GetUsersFavoriteTeam, { headers: authHeaders(), signal });
      return res.data?.data?.UserFavouriteTeam ?? [];
    },
  });
}

/** Unread notification/alert count for the bell badge. */
export function useNotificationsCount({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["v2", "header", "notifications-count", currentUserId()],
    enabled,
    queryFn: async ({ signal }) => {
      const res = await axios.get<{ data: { total_entries?: number } }>(
        APIRoutes.GetLogsCount,
        { headers: authHeaders(), signal }
      );
      return res.data?.data?.total_entries ?? 0;
    },
  });
}

/**
 * The viewer's role id, used to decide admin nav visibility. Returns a
 * `{ data }` shape so call sites read it like the other header queries.
 */
export function useViewerRole({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const token = enabled ? getCurrentAuth()?.AccessToken ?? null : null;
  const data = useMemo(() => roleIdFromToken(token), [token]);
  return { data };
}

/** A saved personal channel as GetAllChannel returns it. */
export interface UserChannel {
  ID: string | number;
  UserID?: string;
  Email?: string;
  Configuration?: {
    Name?: string;
    /** Team ids followed by this channel. */
    Teams?: (string | number)[];
    /** Player ids followed by this channel. */
    Players?: (string | number)[];
    /** Event types this channel collects, e.g. "GOAL", "FACE_OFF". */
    Events?: string[];
  };
  /** True when another user shared this channel with the viewer. */
  IsShared?: boolean;
  SharedByEmail?: string | null;
  updatedAt?: string;
}

/**
 * The viewer's personal channels, used by the header for v1's
 * useCommonDetails isChannelCreated flag (has the viewer created any?).
 * Returns the full records rather than just a count so the shape matches
 * what the endpoint actually sends. Lens reads the same channels through
 * its own paginated endpoint (useChannelList), not this one.
 */
export function useUserChannels({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["v2", "header", "user-channels", currentUserId()],
    enabled,
    queryFn: async ({ signal }) => {
      const res = await axios.get<{ data: UserChannel[] | null }>(
        APIRoutes.GetAllChannel,
        { headers: authHeaders(), signal }
      );
      return res.data?.data ?? [];
    },
  });
}
